import React, { useCallback, useEffect, useState } from 'react';

export const SUBMENU_LAYOUT_KEY = 'mdv-submenu-layout';

export function readSubmenuLayout() {
  try {
    const v = localStorage.getItem(SUBMENU_LAYOUT_KEY);
    if (v === 'column' || v === 'row') return v;
  } catch {
    /* ignore */
  }
  return 'column';
}

export function writeSubmenuLayout(layout) {
  try {
    localStorage.setItem(SUBMENU_LAYOUT_KEY, layout);
  } catch {
    /* ignore */
  }
}

export function useSubmenuLayout() {
  const [layout, setLayoutState] = useState(readSubmenuLayout);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === SUBMENU_LAYOUT_KEY) setLayoutState(readSubmenuLayout());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setLayout = useCallback((next) => {
    const value = typeof next === 'function' ? next(readSubmenuLayout()) : next;
    const resolved = value === 'column' ? 'column' : 'row';
    writeSubmenuLayout(resolved);
    setLayoutState(resolved);
  }, []);

  return [layout, setLayout];
}

/** Shared chrome for Tools / Draw / Section / Preferences submenus. */
export function ToolSubmenu({ layout = 'column', label, children, className = '', onClose }) {
  const isColumn = layout === 'column';

  const closeBtn = onClose ? (
    <button
      type="button"
      title={`Close ${label}`}
      aria-label={`Close ${label}`}
      onClick={onClose}
      className="m-0 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-yellow-600/30 text-zinc-500 hover:border-yellow-400 hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors"
    >
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 4l8 8M12 4L4 12" strokeLinecap="round" />
      </svg>
    </button>
  ) : null;

  return (
    <div
      role="toolbar"
      aria-label={label}
      className={
        isColumn
          ? `flex flex-col gap-1.5 px-2 py-2 border-b border-yellow-600/20 bg-black/90 ${className}`
          : `relative flex-shrink-0 flex items-center gap-1 px-3 py-1 bg-black/90 border-b border-yellow-600/20 overflow-x-auto z-[60] ${className}`
      }
    >
      {isColumn ? (
        <div className="flex w-full items-center justify-between gap-1.5 mb-0.5 shrink-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-yellow-600/70">
            {label}
          </span>
          {closeBtn}
        </div>
      ) : (
        <span className="text-[8px] font-semibold uppercase tracking-[0.14em] text-yellow-600/70 shrink-0 mr-1">
          {label}
        </span>
      )}
      <div
        className={
          isColumn
            ? 'flex flex-col gap-1.5 w-full min-w-0'
            : 'flex items-center gap-1 min-w-0 flex-1'
        }
      >
        {children}
      </div>
      {!isColumn && closeBtn}
    </div>
  );
}

/** Far-right rail when submenu layout is column. */
export function SubmenuRail({ layout, children, hasContent, compact = false }) {
  if (!hasContent) return null;

  if (compact) {
    return (
      <aside
        className="relative z-[80] order-last w-full max-h-[min(42vh,22rem)] shrink-0 overflow-y-auto overscroll-contain border-t border-yellow-600/30 bg-black/95 pointer-events-auto"
        aria-label="Tool submenus"
      >
        {children}
      </aside>
    );
  }

  if (layout === 'column') {
    return (
      <aside
        className="absolute right-0 top-0 bottom-0 z-[60] w-[12.5rem] flex flex-col overflow-y-auto border-l border-yellow-600/25 bg-black/92 pointer-events-auto shadow-[-8px_0_24px_rgba(0,0,0,0.35)]"
        aria-label="Tool submenus"
      >
        {children}
      </aside>
    );
  }

  return <div className="flex flex-col shrink-0 z-[60]">{children}</div>;
}
